/*************************************************************************
 * ADOBE SYSTEMS INCORPORATED
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying it.
 * If you have received this file from a source other than Adobe, then your
 * use, modification, or distribution of it requires the prior written
 * permission of Adobe.
 **************************************************************************/
/**
 * @file
 */

import template from 'url-template';
import uuidv4 from 'uuid/v4';
import AdobeDC from 'adobe-dcapi-web';

const core = window.adobe_dc_sdk.appLauncher.coreFunction();
const { providerUtil, logging, providers } = core;
const getSingletonFunction = providerUtil.getSingletonFunction;

const logger = logging.getLogger('Asset2APIImpl');

const assets = {
  delete: 'assets.delete',
  download: 'assets.download',
  download_uri: 'assets.download_uri',
  get_metadata: 'assets.get_metadata',
  put_metadata_field: 'assets.put_metadata_field',
  upload: 'assets.upload',
  update: 'assets.update',
  zip_download_uri: 'assets.zip_download_uri',
  zip_download: 'assets.zip_download',
  delete_metadata_field: 'assets.delete_metadata_field',
};
const folders = {
  create: 'folders.create',
  delete: 'folders.delete',
  get_metadata: 'folders.get_metadata',
  get_root: 'folders.get_root',
  list: 'folders.list',
  breadcrumbs: 'folders.breadcrumbs',
  put_metadata_field: 'folders.put_metadata_field',
};
const operations = {
  move: 'operations.move',
};

const content = {
  asset: 'asset_v1.json',
  asset_uri_download: 'asset_uri_download_v1.json',
  asset_metadata: 'asset_metadata_basic_v1.json',
  asset_metadata_field: 'asset_metadata_field_v1.json',
  folder: 'folder_v1.json',
  folder_creation: 'folder_creation_v1.json',
  folder_listing: 'folder_listing_v1.json',
  folder_breadcrumbs: 'folder_breadcrumbs_v1.json',
  folder_metadata_basic: 'folder_metadata_basic_v1.json',
  folder_root: 'root_v1.json',
  move_result: 'move_op_result_v1.json',
  move_params: 'move_op_params_v1.json',
  folder_metadata_field: 'folder_metadata_field_v1.json',
  zip_download: 'zip_download_params_v1.json',
  zip_download_uri: 'zip_download_uri_v1.json',
  upload_metadata: 'asset_upload_parameters_v1.json',
  upload: 'multipart/form-data',
};

const accept = {
  application_zip: 'application/zip',
};

const isObject = x => typeof x === 'object';

export interface IFormDataParams {
  boundary?: string;
  content?: string;
  contentType?: string;
  encoding?: string;
  filename?: string;
  name?: string;
  size?: string;
}

export const formDataPart = (dcapi, operation, options) => {
  let parameter;
  const CRLF = '\r\n';
  let names;
  const params: IFormDataParams = { contentType: options.contentType, boundary: options.boundary };
  const formTemplate = [];
  let body = options.body;
  const startBoundary = () => `--${params.boundary}`;
  const endBoundary = () => `--${params.boundary}--`;
  const type = () => `Content-Type: ${params.contentType}`;
  const disposition = () => `Content-Disposition: form-data; name="${params.name}"`;
  const dispositionFile = () => `Content-Disposition: form-data; name="${params.name}"; filename="${params.filename}"`;
  const transferEncoding = () => `Content-Transfer-Encoding: ${params.encoding}`;
  const fileSize = () => `Content-Length: ${params.size}`;

  try {
    names = operation.split('.');
    operation = dcapi.result.content.resources[names[0]][names[1]];
    parameter = operation.form_data_parameters[options.parameterIndex];
  } catch (e) {
    logger.error('Invalid parameters to formDataPart.  Form data parameter not found');
    throw new AdobeDC.AdobeError('Invalid parameters to formDataPart.  Form data parameter not found');
  }
  // for the streaming case -- return the terminating boundary
  if (options.parameterIndex === operation.form_data_parameters.length) {
    return CRLF + endBoundary();
  }

  if (options.parameterIndex > 0) {
    formTemplate.push(''); // force a CRLF in front of the boundary
  }
  formTemplate.push(startBoundary());
  params.contentType = parameter.content_type ? parameter.content_type[options.contentType] : options.contentType;
  formTemplate.push(type());
  params.name = parameter.name;
  if (parameter.type === 'object') {
    formTemplate.push(disposition());
    if (isObject(options.body)) {
      body = JSON.stringify(body);
      // JavaScript strings are stored in utf-16. Here we try to get utf-8 output.
      body = unescape(encodeURIComponent(body));
    }
  }
  if (parameter.type === 'file') {
    // JavaScript strings are stored in utf-16. Here we try to get utf-8 output.
    params.filename = unescape(encodeURIComponent(options.filename));
    formTemplate.push(dispositionFile());
    if (options.size) {
      params.size = options.size;
      formTemplate.push(fileSize());
    }
    if (options.transferEncoding) {
      params.encoding = options.transferEncoding;
      formTemplate.push(transferEncoding());
    }
  }
  formTemplate.push('');
  if (body) {
    params.content = body;
    formTemplate.push(params.content);
    // if it's the last part, add the ending boundary and CRLF
    if (options.parameterIndex + 1 === operation.form_data_parameters.length) {
      formTemplate.push(endBoundary());
      formTemplate.push('');
    }
  } else {
    formTemplate.push('');
  }
  return formTemplate.join(CRLF);
};

let changeListeners = [];

export interface IDCAPIAsset {
  asset_id?: string;
  asset_uri?: string;
  rename_info?: {
    old_name: string;
    new_name: string;
  };
  etag?: string;
  name?: string;
  inputFile?: File;
}

export interface IDCAPIOptions {
  uri_parameters?: {
    make_direct_storage_uri?: boolean;
    make_ticket?: boolean;
    [key: string]: any;
  };
  asset_uri?: string;
  headers?: object;
  on_dup_name?: string;
  content?: object;
}

export interface IAsset2API {
  setRecentChange: (change: any) => void;
  useChange: (listener: any) => void;
  unuseChange: (listener: any) => void;
  ready: () => Promise<IAsset2API | any>;
  uriFromAsset: (asset: IDCAPIAsset) => string;
  uriFromAssetPromise: (asset: IDCAPIAsset) => Promise<string>;
  sendBinaryData: (operation, givenAsset, boundary, filename, parameters, requestPromise?: Promise<any>, dcapi?: any) => Promise<any>;
  uploadFile: (asset: IDCAPIAsset, options: object) => Promise<IDCAPIAsset>;
  getDownloadURI: (asset: IDCAPIAsset, options: IDCAPIOptions) => Promise<any>
  getAssetMetadata: (asset: IDCAPIAsset) => Promise<any>;
  putAssetMetadata: (asset: IDCAPIAsset, options?: IDCAPIOptions) => Promise<any>;
  deleteAsset: (asset: IDCAPIAsset) => Promise<any>;
  getFolderRoot: (options: IDCAPIOptions) => Promise<string>;
  getFolderList: (folderUri: string, options: IDCAPIOptions) => Promise<object>;
  getFolderBreadcrumbs: (folderUri: string, options: IDCAPIOptions) => Promise<object>;
  getFolderMetadata: (folderUri: string, options: IDCAPIOptions) => Promise<object>;
  createFolder: (params: object, options: IDCAPIOptions) => Promise<object>;
  deleteFolder: (folderUri: string) => Promise<object>;
  getAssetURI: (assetId: string) => string;
  getFolderURI: (assetId: string) => string;
  moveObject: (objectUri, targetParent, options: IDCAPIOptions) => Promise<object>;
  putFolderMetadata: (folderUri, fields, options: IDCAPIOptions) => Promise<object>;
  validateFilename: (filename: string) => boolean;
  getZipDownloadUriFromFolder: (folderUri, zipFileName, timeZoneOffsetMinutes, makeTicket) => string;
  getZipDownloadUriForAssets: (assetsToZip, zipFileName, timeZoneOffsetMinutes, makeTicket) => string;
  downloadZip: (zipDownloadUri: string) => Promise<object>;
  deleteAssetMetadata: (asset: IDCAPIAsset, options: IDCAPIOptions) => Promise<any>;
}

/**
 * @classdesc
 * A service provider for asset-related DCAPI operations.
 * @class
 * @see {@link https://createpdf.acrobat.com/static/documentation/dcapi/API/dcapi-static.html|DCAPI}
 */
class Asset2API implements IAsset2API {
  recentChange;
  dcapi;
  static getInstance;

  /**
   * Set a recent change for others to observe.
   */
  setRecentChange(change) {
    if (!change) {
      throw new TypeError('change must be an object');
    }
    changeListeners.forEach(c => c({ newValue: change }));
    this.recentChange = change;
  }

  useChange(listener) {
    changeListeners.push(listener);
  }

  unuseChange(listener) {
    changeListeners = changeListeners.filter(l => l !== listener);
  }

  /**
   * @description
   * Standard provider ready() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when asset provider has been instantiated
   * @public
   */
  ready() {
    return providers.dcapi().then(dcapi => {
      this.dcapi = dcapi.getDcapi();
      return this;
    });
  }

  /**
   * @description
   * Converts asset from its asset_id (URN), if it exists, to a contemporary (i.e. an asset uri with a fresh expiry)
   * asset URI for the specified asset id (URN).
   * @method
   * @param {Asset} asset - Input asset
   * @returns An asset URI (specified asset_uri).  If no asset_uri is present, the a uri created with the asset_id.
   * @private
   */
  uriFromAsset(asset: IDCAPIAsset): string {
    if (!asset.asset_uri) {
      if (!asset.asset_id) {
        throw new AdobeDC.AdobeError('Asset must have either asset_uri or asset_id');
      }
      return this.getAssetURI(asset.asset_id);
    }
    return asset.asset_uri;
  }

  uriFromAssetPromise(asset: IDCAPIAsset): Promise<string> {
    const self = this;
    return new Promise((resolve, reject) => {
      try {
        resolve(self.uriFromAsset(asset));
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * @description
   * Sends binary data for a given operation using request object in requestPromise
   * @public
   */
  sendBinaryData(operation, givenAsset, boundary, filename, parameters, requestPromise?, dcapi?) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(givenAsset.inputFile);
      fileReader.onload = () => {
        let formDataPart1 = '';
        if (parameters) {
          formDataPart1 = formDataPart(
            dcapi || this.dcapi,
            operation,
            {
              parameterIndex: 0,
              contentType: content.upload_metadata,
              boundary,
              body: { options: parameters },
            },
          );
        }

        const fileContent = fileReader.result;
        const formDataPart2 = formDataPart(
          dcapi || this.dcapi,
          operation,
          {
            parameterIndex: parameters ? 1 : 0,
            contentType: givenAsset.inputFile.type || 'application/octet-stream',
            body: fileContent,
            boundary,
            filename,
            size: givenAsset.inputFile.size,
          },
        );

        const sData = formDataPart1 + formDataPart2;
        const nBytes = sData.length;
        const ui8Data = new Uint8Array(nBytes);
        for (let nIdx = 0; nIdx < nBytes; nIdx++) {
          // eslint-disable-next-line no-bitwise
          ui8Data[nIdx] = sData.charCodeAt(nIdx) & 0xff;
        }

        requestPromise.request.send(ui8Data);
        // @ts-ignore TS expects an argument
        resolve();
      };
      fileReader.onerror = () => {
        logger.error('Could not read file for upload or update');
        reject(new Error('Could not read file for upload or update'));
      };
    });
  }

  /**
   * @description
   * Upload a file and receive a DCAPI asset_uri for it.
   * @method
   * @param {Asset} asset - Input asset which is Instance of Asset
   * @param {object} options - Parameters to append to the formData.
   * @returns {Promise<IDCAPIAsset>} - A promise that resolves to the given asset JSON object now set with a asset_uri
   * @public
   */
  uploadFile(givenAsset: IDCAPIAsset, options = {}): Promise<IDCAPIAsset> {
    const boundary = `__MuLtIpArTbOuNdArY${Date.now()}__`;
    const asset: IDCAPIAsset = givenAsset;
    // Create a multi-part upload payload with:
    // part 1: metadata
    // part 2: file contents

    const promise1 = this.dcapi.call(
      assets.upload,
      {
        accept: content.asset,
        content_type: `${content.upload}; boundary=${boundary}`,
        boundary,
        request_content_open: true,
        headers: {
          'x-request-id': uuidv4(),
          'content-type': `multipart/form-data; boundary=${boundary}`,
        },
      },
    );
    const promise2 = promise1.then(result => {
      asset.asset_uri = result.content.uri;
      if (result.content.rename_info) {
        asset.rename_info = result.content.rename_info;
      }
      asset.etag = result?.headers?.etag;
      return asset;
    });

    promise2.request = promise1.request;

    this.sendBinaryData(assets.upload, givenAsset, boundary, givenAsset.name, options, promise1);

    return promise2;
  }

  /**
   * @description
   * Update file's content for a given asset.
   * @method
   * @param {Asset} givenAsset - Input asset which is Instance of Asset, with { asset_id, inputFile }
   * @param options - Parameters for DCAPI that may include additional headers
   * @returns {Promise} - A promise that resolves to the metadata of the updated asset
   * @public
   */
  updateFile(givenAsset, options: IDCAPIOptions = {}) {
    const boundary = `__MuLtIpArTbOuNdArY${Date.now()}__`;
    const asset = givenAsset;
    // Create a multi-part upload payload with:
    // part 1: metadata
    // part 2: file contents

    return this.uriFromAssetPromise(asset).then(assetURI => {
      const updatePromise = this.dcapi.call(
        assets.update,
        {
          content_type: `${content.upload}; boundary=${boundary}`,
          boundary,
          request_content_open: true,
          headers: {
            ...options.headers,
            'x-request-id': uuidv4(),
            'content-type': `multipart/form-data; boundary=${boundary}`,
          },
          uri_parameters: { asset_uri: assetURI },
        },
      );
      let eTagValue = null;
      const metadataPromise = updatePromise.then(updatePromiseValue => {
        eTagValue = updatePromiseValue?.headers?.etag;
        return this.getAssetMetadata({ asset_uri: assetURI });
      });
      metadataPromise.request = updatePromise.request;

      this.sendBinaryData(assets.update, givenAsset, boundary, 'blob', undefined, updatePromise);

      return metadataPromise.then(metaData => {
        metaData.etag = eTagValue;
        return metaData;
      });
    });
  }

  /**
   * @description
   * Get a URI to use for downloading actual file content-bytes.
   * @method
   * @param {Asset} asset - Input asset for getting its download URI
   * @param options - Parameters for DCAPI to get the download URI for given asset
   * @returns {Promise} - A promise that resolves to result containing the download URI
   * @public
   */
  getDownloadURI(asset, options: IDCAPIOptions = {}) {
    options.uri_parameters = options.uri_parameters || {};

    return this.uriFromAssetPromise(asset).then(assetURI => {
      options.asset_uri = assetURI as string;
      return this.dcapi
        .call(assets.download_uri, {
          accept: content.asset_uri_download,
          resource_parameter: assetURI,
          uri_parameters: {
            make_ticket: options.uri_parameters.make_ticket,
            make_direct_storage_uri: options.uri_parameters.make_direct_storage_uri,
          },
        })
        .then(result => {
          asset.downloadURI = result.content.uri;
          return result.content;
        });
    });
  }

  /**
   * @description
   * Get the metadata of the given asset.
   * @method
   * @param {Asset} asset - Input asset whose metadata is to be fetched
   * @returns {Promise} - A promise that resolves to JSON object with meta-data from DCAPI
   * @public
   */
  getAssetMetadata(asset) {
    return this.uriFromAssetPromise(asset).then(assetURI => this.dcapi
      .call(assets.get_metadata, {
        accept: content.asset_metadata,
        resource_parameter: assetURI,
      })
      .then(result => result.content));
  }

  /**
   * @description
   * Update the metadata of the given asset.
   * @method
   * @param {Asset} asset - Input asset whose metadata is to be updated
   * @returns {Promise} - A promise that resolves to JSON object with meta-data from DCAPI
   * @public
   */
  putAssetMetadata(asset: IDCAPIAsset, options: IDCAPIOptions = {}) {
    return this.uriFromAssetPromise(asset).then(assetURI => this.dcapi
      .call(
        assets.put_metadata_field,
        {
          content_type: content.asset_metadata_field,
          resource_parameter: assetURI,
          ...options,
        },
      )
      .then(result => result.content));
  }

  /**
   * @description
   * Delete the given asset.
   * @method
   * @param {Asset} asset - asset with asset_uri to be deleted
   * @returns {Promise} - A promise that resolves to the uri of the deleted asset
   * @public
   */
  deleteAsset(asset) {
    return this.uriFromAssetPromise(asset).then(assetURI => this.dcapi.call(assets.delete, {
      resource_parameter: assetURI,
    }));
  }

  /**
   * @description
   * Get a URI to the root folder.
   * @method
   * @param {object} options - options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to URI to the root folder
   * @public
   */
  getFolderRoot(options) {
    return this.dcapi
      .call(
        folders.get_root,
        {
          accept: content.folder_root,
          ...options,
        },
      )
      .then(result => result.content.root_uri);
  }

  /**
   * @description
   * Lists the folder contents of the given folder.
   * @method
   * @param {string} folderUri - The URI for the folder contents to be listed
   * @param {object} options - the options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to JSON object with the folder structure from DCAPI
   * @public
   */
  getFolderList(folderUri, options) {
    return this.dcapi
      .call(
        folders.list,
        {
          accept: content.folder_listing,
          resource_parameter: folderUri,
          ...options,
        },
      )
      .then(result => result.content);
  }

  /**
   * @description
   * Gets the folder's breadcrumbs (path) from the root folder.
   * @method
   * @param {string} folderUri - The URI of the folder
   * @param {object} options - The options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A Promise that resolves to a JSON object with the folder's breadcrumbs from DCAPI
   * @public
   */
  getFolderBreadcrumbs(folderUri, options) {
    return this.dcapi
      .call(
        folders.breadcrumbs,
        {
          accept: content.folder_breadcrumbs,
          resource_parameter: folderUri,
          ...options,
        },
      )
      .then(result => result.content);
  }

  /**
   * @description
   * Get folder metadata given a folder_uri.
   * @method
   * @param {string} folderUri - The URI of the folder
   * @param {object} options - the options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to folder metadata
   * @public
   */
  getFolderMetadata(folderUri, options) {
    return this.dcapi
      .call(
        folders.get_metadata,
        {
          accept: content.folder_metadata_basic,
          resource_parameter: folderUri,
          ...options,
        },
      )
      .then(result => result.content);
  }

  /**
   * @description
   * Create a folder under the given folder_uri.
   * @method
   * @param {object} parameters - input parameters, e.g. {parent_uri, name}
   * @param {object} options - parameters to append to the formData.
   * @returns {Promise} - A promise that resolves to asset JSON object now set with a asset_uri
   * @public
   */
  createFolder(parameters, options) {
    return this.dcapi
      .call(
        folders.create,
        {
          accept: content.folder,
          content_type: content.folder_creation,
          content: parameters,
          ...options,
        },
      )
      .then(result => result.content);
  }

  /**
   * @description
   * Delete a folder matching the given folder_uri
   * @method
   * @param {object} options - the options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to the deleted folderUri
   * @public
   */
  deleteFolder(folderUri) {
    return this.dcapi.call(folders.delete, {
      resource_parameter: folderUri,
    });
  }

  /**
   * @description
   * Given an asset id, get an asset URI.
   * @method
   * @param {string} assetId - The asset id value (actually, an asset URN)
   * @returns {string} - an asset URI
   * @public
   */
  getAssetURI(assetId) {
    if (!assetId) {
      throw new AdobeDC.AdobeError('assetId is required');
    }
    const assetTemplate = template.parse(this.dcapi.templates.asset_uri);
    return assetTemplate.expand({ asset_id: assetId });
  }

  /**
   * @description
   * Given a folder id, get a folder URI.
   * @method
   * @param {string} folderId - The folder id value (actually, an folder URN)
   * @returns {string} - a folder URI
   * @public
   */
  getFolderURI(folderId) {
    const folderTemplate = template.parse(this.dcapi.templates.folder_uri);
    return folderTemplate.expand({ folder_id: folderId });
  }

  /**
   * @description
   * Move either a file or folder
   * @method
   * @param {string} objectUri - A file or a folder to be moved.
   * @param {object} targetParent - The parent object where the object is to be moved to. Contains a uri and a name
   * @param {Object} [options] - Options such as on_dup_name(How to handle duplicate name) can be provided.
   * @returns {Promise} - A promise that resolves to
   * @public
   */
  moveObject(objectUri, targetParent, options: IDCAPIOptions = {}) {
    const moveOptions = {
      source: {
        object_uri: objectUri,
      },
      target: {
        parent_uri: targetParent.uri,
        name: targetParent.name,
      },
      on_dup_name: options.on_dup_name || 'auto_rename',
      ...options,
    };
    return this.dcapi.call(operations.move, {
      accept: content.move_result,
      content_type: content.move_params,
      content: moveOptions,
    });
  }

  /**
   * @description
   * Update the starred and favorite fields of metadata for a folder
   * @method
   * @param {string} folderUri - The uri of the folder whose metadata is to be updated.
   * @param {string} fields - Single field name or comma separated names of metadata fields to update,
   * Eg: fields = "starred" or fields = "starred, favorite"
   * @param {Object} [options] - Optional fields that can help in identifying the folder, eg {name: "", uri: ""}
   * The options also contains the key, value pairs for the fields if any of the field is being updated
   * options: {starred: true, favorite: false}
   * @public
   */
  putFolderMetadata(folderUri, fields, options: IDCAPIOptions = {}) {
    return this.dcapi.call(folders.put_metadata_field, {
      content_type: content.folder_metadata_field,
      resource_parameter: folderUri,
      uri_parameters: { fields },
      content: options,
    });
  }

  /**
   * @description
   * Validate that a filename is operating system safe and DOM safe.
   * This logic is based on Blue Heron's file_utils.validate_name_legal()
   * File names will be tested with whitespace stripped.
   * @method
   * @param {string} filename - The filename to be validated with the extension.
   * @example
   * validateFilename("SampleFilename.pdf") returns true
   * Any of the following conditions will return false:
   *    1. Length < 1 or > 255
   *    2. Contains any of <>:\/"?|*
   *    3. Contains unicode control characters U+0000 - U+001F
   *    4. Begins or ends with '.' (including "." and "..")
   *    5. Is equal to one of "CON", "PRN", "AUX", "NUL", "COM{n}", "LPT{n}"
   */
  validateFilename(filename) {
    // eslint-disable-next-line no-control-regex
    const badChars = /["*:?|/\\<>\u0000-\u001F]/;
    const notAllowedFileNameList = [
      'CON',
      'PRN',
      'AUX',
      'NUL',
      'COM0',
      'COM1',
      'COM2',
      'COM3',
      'COM4',
      'COM5',
      'COM6',
      'COM7',
      'COM8',
      'COM9',
      'LPT0',
      'LPT1',
      'LPT2',
      'LPT3',
      'LPT4',
      'LPT5',
      'LPT6',
      'LPT7',
      'LPT8',
      'LPT9',
    ];
    filename = filename.trim();
    if (
      !filename
          || filename.length > 255
          || filename === '.'
          || filename === '..'
          || notAllowedFileNameList.includes(filename.toUpperCase())
          || filename.charAt(0) === '.'
          || filename.slice(-1) === '.'
          || badChars.test(filename)
    ) {
      return false;
    }
    return true;
  }

  /**
   * @description
   * Method to download multiple files or a single folder as a zip file
   * @method
   * @param {string} [folderUri] - The folder to create zip file from.
   * @param {string} zipFileName - Optional name to give to the zip file.
   * Default name is the name of the folder provided.
   * @param {number} timeZoneOffsetMinutes - Optional time zone offset of client's local time from GMT
   * Allows client to get zip file with client local modified times.
   * The default timezone offset is GMT i.e. 0, min. is -1440 and max. is 1440.
   * @param {boolean} makeTicket - Optional parameter to generate a download ticket valid for 1 minute
   * included in the uri. Defaults to false. Usually true for URLs used from browser.
   * @returns {string} - The download uri for the zip file created.
   */
  getZipDownloadUriFromFolder(folderUri, zipFileName, timeZoneOffsetMinutes, makeTicket) {
    const sourceContent = {
      folder_uri: folderUri,
    };
    return this._getZipDownloadUri(sourceContent, zipFileName, timeZoneOffsetMinutes, makeTicket);
  }

  /**
   * @description
   * Method to download multiple files as a zip file
   * Works with transient assets!
   *
   * @method
   * @param {Array<string>} assetsToZip - Array of asset_id's to bundle as a zip file
   * @param {string} zipFileName - Optional name to give to the zip file. (default - documents.zip downstream)
   * @param {number} timeZoneOffsetMinutes - Optional time zone offset of client's local time from GMT
   * Allows client to get zip file with client local modified times.
   * The default timezone offset is GMT i.e. 0, min. is -1440 and max. is 1440.
   * @param {boolean} makeTicket - Optional parameter to generate a download ticket valid for 1 minute
   * included in the uri. Defaults to false. Usually true for URLs used from browser.
   * @returns {string} - The download uri for the zip file created.
   */
  getZipDownloadUriForAssets(assetsToZip, zipFileName, timeZoneOffsetMinutes, makeTicket) {
    const sourceContent = {
      asset_uris: assetsToZip,
    };
    return this._getZipDownloadUri(sourceContent, zipFileName, timeZoneOffsetMinutes, makeTicket);
  }

  _getZipDownloadUri(sourceContent, zipFileName = 'documents.zip', timeZoneOffsetMinutes = 0, makeTicket = false) {
    return this.dcapi.call(assets.zip_download_uri, {
      accept: content.zip_download_uri,
      content_type: content.zip_download,
      content: Object.assign(sourceContent, {
        zip_file_name: zipFileName,
        time_zone_offset_minutes: timeZoneOffsetMinutes,
        make_ticket: makeTicket,
      }),
    });
  }

  /**
   * Method to download a zip file given a zip_download_uri
   * @emthod
   * @param {string} zipDownloadUri - The uri received from getZipDownloadUri operation
   * @returns {object} - The zip file.
  */
  downloadZip(zipDownloadUri) {
    return this.dcapi.call(assets.zip_download, {
      accept: accept.application_zip,
      resource_parameter: zipDownloadUri,
    });
  }

  /**
   * @description
   * Delete selected metadata fields associated with an asset.
   * @method
   * @param {Asset} asset - Input asset whose metadata is to be deleted
   * @returns {Promise} - A promise that resolves to JSON object with meta-data from DCAPI
   * @public
   */
  deleteAssetMetadata(asset: IDCAPIAsset, options: IDCAPIOptions = {}) {
    return this.uriFromAssetPromise(asset).then(assetURI => this.dcapi
      .call(
        assets.delete_metadata_field,
        {
          resource_parameter: assetURI,
          ...options,
        },
      )
      .then(result => result.content));
  }
}

// This allows for providers.x().then() to be called before providers.x(config).
Asset2API.getInstance = getSingletonFunction(Asset2API);
export default Asset2API;
